import React, { Dispatch, SetStateAction, useState } from "react";
import {
  Box,
  Button,
  SxProps,
  Theme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useDropzone } from "react-dropzone";

const styles: { [K in string]: SxProps<Theme> } = {
  fileUploadBox: {
    position: "relative",
    width: 300,
    height: 100,
    border: "2px dashed var(--silver-500)",
    padding: "20px",
    textAlign: "center",
    cursor: "pointer",
  },
};

type Props = {
  isOpenCsvUpload: boolean;
  isCheck: boolean;
  onClose: Function;
  goConfirm: Function;
  file: File | null;
  title: string;
  isLoading: boolean;
  setFile: Dispatch<SetStateAction<File | null>>;
};

/**
 * CSVアップロード画面
 * @param param0 コンポーネントのプロップス
 * @returns JSX.Element
 */
export default function CsvUploadModal({
  isOpenCsvUpload,
  isCheck,
  onClose,
  goConfirm,
  file,
  title,
  isLoading,
  setFile,
}: Props) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const onDrop = (acceptedFiles: File[]) => {
    const selectedFile = acceptedFiles[0];

    if (!selectedFile) return;

    // CSVファイルのサイズバリデーション
    const fileSizeMB = selectedFile.size / 1024 / 1024;

    if (fileSizeMB > 1) {
      setErrorMessage("サイズは1MBまでです。\n正しいファイルを選択してください。");
      return;
    }

    // 正常なファイルの場合
    setFile(selectedFile);
    setErrorMessage(null);
  };

  const onDropRejected = () => {
    // CSV以外のファイルがドロップされた場合の処理
    setErrorMessage(
      "サポートされているファイル形式はCSVのみです。\n正しいファイルを選択してください。",
    );
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    onDropRejected,
    accept: { "text/csv": [] },
    multiple: false,
  });

  // エラーメッセージも空にする
  const onCloseModal = () => {
    setTimeout(() => {
      setErrorMessage(null);
    }, 200);
    onClose();
  };

  return (
    <Dialog open={isOpenCsvUpload && !isCheck} onClose={() => onCloseModal()}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={styles.fileUploadBox} {...getRootProps()}>
          <Box
            position="absolute"
            top="0"
            left="0"
            width="100%"
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
          >
            <Box>{!file ? "ファイルを選択またはドラッグ&ドロップ" : file.name}</Box>
            {errorMessage && (
              <p style={{ fontSize: "14px", color: "var(--red-800)" }}>
                {errorMessage.split("\n").map((line) => (
                  <React.Fragment key={line}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </p>
            )}
          </Box>
          <input {...getInputProps()} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="custom-outlined" onClick={onCloseModal} disabled={isLoading}>
          キャンセル
        </Button>
        <Button variant="contained" onClick={() => goConfirm()} disabled={!file || isLoading}>
          内容を確認
        </Button>
      </DialogActions>
    </Dialog>
  );
}
